import { Card, ClickState, HandStatus } from "../GameTypes/game.type";

const suits: string[] = ["hearts", "diamonds", "clubs", "spades"];
const values: (number | string)[] = [
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  "J",
  "Q",
  "K",
  "A",
];

const createDeck = (): Card[] => {
  let deck: Card[] = [];
  for (let suit of suits) {
    for (let value of values) {
      deck.push({ suit, value });
    }
  }
  //*******Split functionality test code*******//

  //   let groupedDeck = [];
  //   for (let value of values) {
  //     let group = deck.filter((card) => card.value === value);
  //     groupedDeck.push(group);
  //   }
  //   return groupedDeck.sort(() => Math.random() - 0.5).flat();
  return deck.sort(() => Math.random() - 0.5);
};

const calculateHandValue = (
  cards: Card[],
  dealerReveal: boolean = true
): number => {
  let total = 0;
  let aces = 0;

  cards.forEach((card, index) => {
    if (!dealerReveal && index === 1) return;

    if (["J", "Q", "K"].includes(card.value as string)) {
      total += 10;
    } else if (card.value === "A") {
      total += 11;
      aces += 1;
    } else {
      total += card.value as number;
    }
  });

  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }

  return total;
};

const determineWinner = (
  dealerTotalValue: number,
  playerCards: Card[][],
  isSplit: boolean,
  setHandStatuses: React.Dispatch<React.SetStateAction<HandStatus[]>>,
  setGameStatus: React.Dispatch<React.SetStateAction<string>>
) => {
  const primaryTotal = calculateHandValue(playerCards[0]);
  const splitTotal = isSplit ? calculateHandValue(playerCards[1]) : 0;

  setHandStatuses((prev: HandStatus[]) => {
    const newStatuses = [...prev];

    if (
      primaryTotal > 21 ||
      (dealerTotalValue <= 21 && dealerTotalValue > primaryTotal)
    ) {
      newStatuses[0] = "lost";
    } else if (dealerTotalValue > 21 || primaryTotal > dealerTotalValue) {
      newStatuses[0] = "won";
    } else {
      newStatuses[0] = "draw";
    }

    if (isSplit && playerCards[1].length > 0) {
      if (
        splitTotal > 21 ||
        (dealerTotalValue < 21 && dealerTotalValue > splitTotal)
      ) {
        newStatuses[1] = "lost";
      } else if (dealerTotalValue > 21 || splitTotal > dealerTotalValue) {
        newStatuses[1] = "won";
      } else {
        newStatuses[1] = "draw";
      }
    }

    return newStatuses;
  });

  const gameOutcome =
    primaryTotal > 21 && (!isSplit || splitTotal > 21)
      ? "player lost"
      : dealerTotalValue > 21
      ? "player won"
      : primaryTotal > dealerTotalValue ||
        (isSplit && splitTotal > dealerTotalValue)
      ? "player won"
      : primaryTotal < dealerTotalValue &&
        (!isSplit || splitTotal < dealerTotalValue)
      ? "dealer won"
      : "draw";

  setGameStatus(gameOutcome);
};

const startGame = (
  playSound: HTMLAudioElement,
  setIsDealing: React.Dispatch<React.SetStateAction<boolean>>,
  setDeck: React.Dispatch<React.SetStateAction<Card[]>>,
  setPlayerCards: React.Dispatch<React.SetStateAction<Card[][]>>,
  setDealerCards: React.Dispatch<React.SetStateAction<Card[]>>,
  setFlippedCards: React.Dispatch<React.SetStateAction<number[]>>,
  setDealingIndex: React.Dispatch<React.SetStateAction<number>>,
  setDealerSecondCardRevealed: React.Dispatch<React.SetStateAction<boolean>>,
  setHasPlayerStand: React.Dispatch<React.SetStateAction<boolean[]>>,
  setGameStatus: React.Dispatch<React.SetStateAction<string>>,
  setHasDoubledDown: React.Dispatch<React.SetStateAction<[boolean, boolean]>>,
  setShowInsurance: React.Dispatch<React.SetStateAction<boolean>>,
  setHasTakenInsurance: React.Dispatch<React.SetStateAction<boolean>>,
  setInsuranceBet: React.Dispatch<React.SetStateAction<number>>,
  setIsSplit: React.Dispatch<React.SetStateAction<boolean>>,
  setSplitHands: React.Dispatch<React.SetStateAction<boolean>>,
  setHandIndex: React.Dispatch<React.SetStateAction<number>>,
  setHandStatuses: React.Dispatch<React.SetStateAction<HandStatus[]>>,
  setGameButtonClicked: React.Dispatch<React.SetStateAction<ClickState>>
) => {
  playSound.play();
  setTimeout(() => {
    setIsDealing(true);
    setDeck(createDeck());
    setPlayerCards([[], []]);
    setDealerCards([]);
    setFlippedCards([]);
    setDealingIndex(0);
    setDealerSecondCardRevealed(false);
    setHasPlayerStand([false, false]);
    setGameStatus("playing");
    setHasDoubledDown([false, false]);
    setShowInsurance(false);
    setHasTakenInsurance(false);
    setInsuranceBet(0);
    setIsSplit(false);
    setSplitHands(false);
    setHandIndex(0);
    setHandStatuses(["", ""]);
    setGameButtonClicked({
      hit: false,
      stand: false,
      split: false,
      double: false,
      play: true,
    });
  }, 500);
};

const handleGameButtonClick = (
  gameButtonClicked: ClickState,
  setGameButtonClicked: React.Dispatch<React.SetStateAction<ClickState>>,
  startGameFn: () => void
) => {
  if (!gameButtonClicked.play) {
    setGameButtonClicked({
      hit: false,
      stand: false,
      split: false,
      double: false,
      play: true,
    });
    startGameFn();
  }
};

const handleSplit = (
  playerCards: Card[][],
  deck: Card[],
  dealingIndex: number,
  setIsSplit: React.Dispatch<React.SetStateAction<boolean>>,
  setPlayerCards: React.Dispatch<React.SetStateAction<Card[][]>>,
  setFlippedCards: React.Dispatch<React.SetStateAction<number[]>>,
  setDeck: React.Dispatch<React.SetStateAction<Card[]>>,
  setDealingIndex: React.Dispatch<React.SetStateAction<number>>,
  distributeSound: HTMLAudioElement,
  runAnimation: (
    isPlayer: boolean,
    dealerCardCount: number,
    index: number
  ) => void,
  dealerCards: Card[]
) => {
  if (
    playerCards[0].length !== 2 ||
    playerCards[0][0].value !== playerCards[0][1].value
  ) {
    return;
  }

  setIsSplit(true);
  const firstHand = [playerCards[0][0]];
  const secondHand = [playerCards[0][1]];
  setPlayerCards([firstHand, secondHand]);
  const firstCardId = Date.now() + dealingIndex;
  const secondCardId = Date.now() + dealingIndex + 1;

  setTimeout(() => {
    distributeSound.play();
    runAnimation(true, dealerCards.length, 0);
    setTimeout(() => {
      setPlayerCards((prev: Card[][]) => {
        const updatedHands = [...prev];
        updatedHands[0] = [
          ...updatedHands[0],
          { ...deck[dealingIndex], id: firstCardId },
        ];
        return updatedHands;
      });
      setFlippedCards((prev: number[]) => [...prev, firstCardId]);
      setTimeout(() => {
        setFlippedCards((prev: number[]) =>
          prev.filter((id) => id !== firstCardId)
        );
      }, 200);
    }, 150);
  }, 300);

  setTimeout(() => {
    distributeSound.play();
    runAnimation(true, dealerCards.length, 1);
    setTimeout(() => {
      setPlayerCards((prev: Card[][]) => {
        const updatedHands = [...prev];
        updatedHands[1] = [
          ...updatedHands[1],
          { ...deck[dealingIndex + 1], id: secondCardId },
        ];
        return updatedHands;
      });
      setFlippedCards((prev: number[]) => [...prev, secondCardId]);
      setTimeout(() => {
        setFlippedCards((prev: number[]) =>
          prev.filter((id) => id !== secondCardId)
        );
        setDeck(deck.slice(dealingIndex + 2));
        setDealingIndex(dealingIndex + 2);
      }, 200);
    }, 150);
  }, 700);
};

const handleAcceptInsurance = (
  setHasTakenInsurance: React.Dispatch<React.SetStateAction<boolean>>,
  setInsuranceBet: React.Dispatch<React.SetStateAction<number>>,
  setShowInsurance: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setHasTakenInsurance(true);
  setInsuranceBet(0.5);
  setShowInsurance(false);
};

const handleDeclineInsurance = (
  setShowInsurance: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setShowInsurance(false);
};

const handleHit = (
  gameStatus: string,
  gameButtonClicked: ClickState,
  deck: Card[],
  dealingIndex: number,
  handIndex: number,
  setGameButtonClicked: React.Dispatch<React.SetStateAction<ClickState>>,
  setPlayerCards: React.Dispatch<React.SetStateAction<Card[][]>>,
  setFlippedCards: React.Dispatch<React.SetStateAction<number[]>>,
  setDealingIndex: React.Dispatch<React.SetStateAction<number>>,
  distributeSound: HTMLAudioElement,
  runAnimation: (
    isPlayer: boolean,
    dealerCardCount: number,
    handIndex: number
  ) => void,
  dealerCards: Card[]
) => {
  if (gameStatus !== "playing" || gameButtonClicked.hit) return;

  setGameButtonClicked((prev: ClickState) => ({ ...prev, hit: true }));
  const newCard = deck[dealingIndex];
  const cardId = Date.now() + dealingIndex;

  distributeSound.play();
  runAnimation(true, dealerCards.length, handIndex);
  setTimeout(() => {
    setPlayerCards((prev: Card[][]) => {
      const updatedHands = [...prev];
      updatedHands[handIndex] = [
        ...updatedHands[handIndex],
        { ...newCard, id: cardId },
      ];
      return updatedHands;
    });
    setFlippedCards((prev: number[]) => [...prev, cardId]);

    setTimeout(() => {
      setFlippedCards((prev: number[]) => prev.filter((id) => id !== cardId));
      setGameButtonClicked((prev: ClickState) => ({ ...prev, hit: false }));
    }, 200);

    setDealingIndex((prev: number) => prev + 1);
  }, 150);
};

const handleDoubleDown = (
  gameStatus: string,
  playerCards: Card[][],
  handIndex: number,
  hasDoubledDown: [boolean, boolean],
  isSplit: boolean,
  deck: Card[],
  dealingIndex: number,
  setHasDoubledDown: React.Dispatch<React.SetStateAction<[boolean, boolean]>>,
  setGameButtonClicked: React.Dispatch<React.SetStateAction<ClickState>>,
  setPlayerCards: React.Dispatch<React.SetStateAction<Card[][]>>,
  setFlippedCards: React.Dispatch<React.SetStateAction<number[]>>,
  setDealingIndex: React.Dispatch<React.SetStateAction<number>>,
  distributeSound: HTMLAudioElement,
  runAnimation: (
    isPlayer: boolean,
    dealerCardCount: number,
    handIndex: number
  ) => void,
  handleStand: () => void,
  dealerCards: Card[]
) => {
  if (
    gameStatus !== "playing" ||
    playerCards[handIndex].length !== 2 ||
    hasDoubledDown[handIndex]
  )
    return;

  setHasDoubledDown((prev: [boolean, boolean]) => {
    const newDoubledDown: [boolean, boolean] = [...prev];
    newDoubledDown[handIndex] = true;
    return newDoubledDown;
  });

  if (!isSplit) {
    setGameButtonClicked((prev: ClickState) => ({
      ...prev,
      hit: true,
      stand: true,
    }));
  } else {
    setGameButtonClicked((prev: ClickState) => ({
      ...prev,
      hit: true,
      stand: true,
    }));
  }

  const newCard = deck[dealingIndex];
  const cardId = Date.now() + dealingIndex;

  distributeSound.play();
  runAnimation(true, dealerCards.length, handIndex);
  setTimeout(() => {
    setPlayerCards((prev: Card[][]) => {
      const updatedHands = [...prev];
      updatedHands[handIndex] = [
        ...updatedHands[handIndex],
        { ...newCard, id: cardId },
      ];
      return updatedHands;
    });
    setFlippedCards((prev: number[]) => [...prev, cardId]);

    setTimeout(() => {
      setFlippedCards((prev: number[]) => prev.filter((id) => id !== cardId));
    }, 200);
    setDealingIndex((prev: number) => prev + 1);
  }, 150);

  setTimeout(() => {
    handleStand();
  }, 800);
};

const handleStand = (
  handIndex: number,
  isSplit: boolean,
  dealerCards: Card[],
  deck: Card[],
  dealingIndex: number,
  setHasPlayerStand: React.Dispatch<React.SetStateAction<boolean[]>>,
  setDealerSecondCardRevealed: React.Dispatch<React.SetStateAction<boolean>>,
  setDealerCards: React.Dispatch<React.SetStateAction<Card[]>>,
  setFlippedCards: React.Dispatch<React.SetStateAction<number[]>>,
  distributeSound: HTMLAudioElement,
  runAnimation: (
    isPlayer: boolean,
    dealerCardCount: number,
    handIndex: number
  ) => void
) => {
  setHasPlayerStand((prev: boolean[]) => {
    const newStand = [...prev];
    newStand[handIndex] = true;
    return newStand;
  });

  if (!isSplit || handIndex === 0) {
    setDealerSecondCardRevealed(true);
    let dealerCardsCopy = [...dealerCards];
    let dealerTotalValue = calculateHandValue(dealerCardsCopy, true);
    let index = dealingIndex;

    const revealNextDealerCard = () => {
      if (dealerTotalValue < 17) {
        const newCard = deck[index];
        const cardId = Date.now() + index;

        distributeSound.play();
        runAnimation(false, dealerCardsCopy.length, handIndex);

        setTimeout(() => {
          dealerCardsCopy.push({ ...newCard, id: cardId });
          setDealerCards([...dealerCardsCopy]);
          setFlippedCards((prev: number[]) => [...prev, cardId]);
          setTimeout(() => {
            setFlippedCards((prev: number[]) =>
              prev.filter((id) => id !== cardId)
            );
            dealerTotalValue = calculateHandValue(dealerCardsCopy, true);
            index++;
            if (dealerTotalValue < 17) {
              setTimeout(revealNextDealerCard, 300);
            }
          }, 200);
        }, 150);
      }
    };

    revealNextDealerCard();
  }
};

export {
  createDeck,
  calculateHandValue,
  determineWinner,
  startGame,
  handleGameButtonClick,
  handleSplit,
  handleAcceptInsurance,
  handleDeclineInsurance,
  handleHit,
  handleDoubleDown,
  handleStand,
};
